import { type RefObject, useLayoutEffect, useState } from 'react';

/** Keeps the menu clear of the window edges, matching `ContextMenu`. */
const VIEWPORT_PADDING = 8;
/** Gap between the trigger and the menu. */
const MENU_OFFSET = 4;

export interface AnchoredMenuPosition {
  top: number;
  left: number;
}

export interface ComputeBottomStartPositionArgs {
  anchor: Pick<DOMRect, 'top' | 'left' | 'bottom'>;
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  offset?: number;
  padding?: number;
}

/**
 * `bottom-start` placement: left edges align, opens downward, flips above
 * when there isn't room below. Pure so the scroll/resize path can re-run
 * it without a DOM round-trip in tests.
 */
export const computeBottomStartPosition = ({
  anchor,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
  offset = MENU_OFFSET,
  padding = VIEWPORT_PADDING,
}: ComputeBottomStartPositionArgs): AnchoredMenuPosition => {
  const below = anchor.bottom + offset;
  const above = anchor.top - offset - menuHeight;
  const fitsBelow = below + menuHeight <= viewportHeight - padding;
  const top =
    fitsBelow || above < padding
      ? Math.min(
          below,
          Math.max(padding, viewportHeight - menuHeight - padding)
        )
      : above;

  return {
    top,
    left: Math.max(
      padding,
      Math.min(anchor.left, viewportWidth - menuWidth - padding)
    ),
  };
};

export interface UseAnchoredMenuPositionArgs {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  menuRef: RefObject<HTMLElement | null>;
}

/**
 * Positions a portalled menu relative to a trigger with `bottom-start`
 * placement. Re-places on scroll / resize while open.
 */
export const useAnchoredMenuPosition = ({
  open,
  triggerRef,
  menuRef,
}: UseAnchoredMenuPositionArgs): AnchoredMenuPosition => {
  const [position, setPosition] = useState<AnchoredMenuPosition>({
    top: 0,
    left: 0,
  });

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    const place = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;

      if (!trigger || !menu) {
        return;
      }

      setPosition(
        computeBottomStartPosition({
          anchor: trigger.getBoundingClientRect(),
          menuWidth: menu.offsetWidth,
          menuHeight: menu.offsetHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      );
    };

    place();

    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, triggerRef, menuRef]);

  return position;
};
